import { useCallback, useEffect, useMemo, useState } from "react";
import {
  PriceListLine,
  PriceListPriceBreak,
  PricingUnitSchema,
  type DimensionUnit,
  type ItemUnit,
  type ItemUnitConversion,
  type PricingUnit,
  type UpdateItemParams,
  type WeightUnit,
  buildConversionContext,
  formatItemUnit,
  formatPricingUnit,
  resolveConversionFactor,
  withLineSuppliedMeasurements,
  enabledItemOverrideFields,
  type ItemOverrideField,
} from "@forgepoint/shared";
import { Edit, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ItemSelector } from "@/components/ItemSelector";
import ItemEditor from "@/modules/items/ItemEditor";
import { useItemsStore } from "@/modules/items/itemsStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { usePriceListsStore } from "./priceListsStore";

/** Subset of item fields needed to determine allowed pricing units. */
interface PricingUnitItemInfo {
  unit: ItemUnit;
  weightUnit?: WeightUnit | null;
  weight?: string | null;
  length?: string | null;
  width?: string | null;
  dimensionUnit?: DimensionUnit | null;
  unitConversions?: ItemUnitConversion[];
}

/** Result of computing which pricing units are available for an item. */
interface AllowedPricingUnitsResult {
  units: PricingUnit[];
  helperText: string | null;
}

/**
 * Returns the pricing units the conversion engine can reach from the item
 * unit, using the item's measurements and unit conversions, plus the fields a
 * quote line can override.
 */
function getAllowedPricingUnits(
  item: PricingUnitItemInfo | null,
  lineFields: ItemOverrideField[],
): AllowedPricingUnitsResult {
  if (!item) {
    return { units: [], helperText: null };
  }

  const context = withLineSuppliedMeasurements(
    buildConversionContext(item),
    lineFields,
  );
  const units = PricingUnitSchema.options.filter(
    (unit) =>
      unit !== item.unit &&
      resolveConversionFactor(item.unit, unit, context) !== null,
  );

  return { units, helperText: getHelperText(units, lineFields) };
}

function getHelperText(
  units: PricingUnit[],
  lineFields: ItemOverrideField[],
): string {
  if (units.length === 0) {
    return "Set weight, dimensions or a unit conversion on the item to enable pricing unit overrides.";
  }
  return lineFields.length > 0
    ? "Converted from the item's measurements, unit conversions, and sizes set on quote lines."
    : "Converted from the item's measurements and unit conversions.";
}

interface PriceListLineEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceListId: string;
  existingLine?: PriceListLine;
  onSaved: () => void;
}

/** Sheet for creating or editing a single price list line's price breaks. */
export default function PriceListLineEditorSheet({
  open,
  onOpenChange,
  priceListId,
  existingLine,
  onSaved,
}: PriceListLineEditorSheetProps) {
  const { addPriceListLines, updatePriceListLine } = usePriceListsStore();
  const { items, updateItem, fetchItem } = useItemsStore();
  const quoteSettings = useQuoteSettingsStore((state) => state.settings);
  const fetchQuoteSettings = useQuoteSettingsStore(
    (state) => state.fetchSettings,
  );
  useEffect(() => {
    if (!quoteSettings) {
      fetchQuoteSettings();
    }
  }, [quoteSettings, fetchQuoteSettings]);
  const lineFields = enabledItemOverrideFields(quoteSettings);

  const [itemId, setItemId] = useState<string | null>(null);
  const [unit, setUnit] = useState<PricingUnit | null>(null);
  const [priceBreaks, setPriceBreaks] = useState<PriceListPriceBreak[]>([
    { minQty: 0, unitPrice: "" },
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [isItemEditorOpen, setIsItemEditorOpen] = useState(false);

  const isEditing = !!existingLine;
  const effectiveItemId = itemId ?? existingLine?.itemId ?? null;
  const selectedItem = effectiveItemId ? items.get(effectiveItemId) : null;

  const itemInfo = useMemo((): PricingUnitItemInfo | null => {
    if (selectedItem) {
      return {
        unit: selectedItem.unit,
        weightUnit: selectedItem.weightUnit,
        weight: selectedItem.weight,
        length: selectedItem.length,
        width: selectedItem.width,
        dimensionUnit: selectedItem.dimensionUnit,
        unitConversions: selectedItem.unitConversions,
      };
    }
    if (existingLine?.item?.unit) {
      return {
        unit: existingLine.item.unit,
        weightUnit: existingLine.item.weightUnit,
        weight: existingLine.item.weight,
        length: existingLine.item.length,
        width: existingLine.item.width,
        dimensionUnit: existingLine.item.dimensionUnit,
        unitConversions: existingLine.item.unitConversions,
      };
    }
    return null;
  }, [selectedItem, existingLine]);

  const { units: allowedUnits, helperText } = getAllowedPricingUnits(
    itemInfo,
    lineFields,
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (existingLine) {
      setItemId(existingLine.itemId);
      setUnit(existingLine.unit ?? null);
      setPriceBreaks(
        existingLine.priceBreaks.length > 0
          ? existingLine.priceBreaks.map((pb) => ({ ...pb }))
          : [{ minQty: 0, unitPrice: "" }],
      );
    } else {
      setItemId(null);
      setUnit(null);
      setPriceBreaks([{ minQty: 0, unitPrice: "" }]);
    }
  }, [existingLine, open]);

  useEffect(() => {
    // Wait for settings, or a line-supplied unit is cleared before it can load.
    if (quoteSettings && unit && !allowedUnits.includes(unit)) {
      setUnit(null);
    }
  }, [allowedUnits, unit, quoteSettings]);

  const handlePriceBreakChange = (
    index: number,
    field: keyof PriceListPriceBreak,
    value: string,
  ) => {
    setPriceBreaks((current) =>
      current.map((pb, i) =>
        i === index
          ? {
              ...pb,
              [field]: field === "minQty" ? Math.max(0, Number(value)) : value,
            }
          : pb,
      ),
    );
  };

  const handleAddPriceBreak = () => {
    const maxQty = Math.max(0, ...priceBreaks.map((pb) => pb.minQty));
    setPriceBreaks((current) => [
      ...current,
      { minQty: maxQty + 1, unitPrice: "" },
    ]);
  };

  const handleRemovePriceBreak = (index: number) => {
    setPriceBreaks((current) => current.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const validBreaks = priceBreaks.filter(
      (pb) => pb.unitPrice.trim() !== "" && pb.minQty >= 0,
    );

    if (validBreaks.length === 0) {
      toast.error("At least one price break with a unit price is required");
      return;
    }

    const sortedBreaks = [...validBreaks].sort((a, b) => a.minQty - b.minQty);

    setIsSaving(true);

    if (isEditing) {
      const result = await updatePriceListLine(priceListId, existingLine!.id, {
        itemId:
          itemId !== existingLine!.itemId ? (itemId ?? undefined) : undefined,
        priceBreaks: sortedBreaks,
        unit,
      });

      if (result) {
        toast.success("Line updated");
        onSaved();
      } else {
        toast.error("Failed to update line");
      }
    } else {
      if (!itemId) {
        toast.error("Please select an item");
        setIsSaving(false);
        return;
      }

      const result = await addPriceListLines(priceListId, {
        lines: [{ itemId, priceBreaks: sortedBreaks, unit }],
      });

      if (result) {
        toast.success("Line added");
        onSaved();
      } else {
        toast.error("Failed to add line");
      }
    }

    setIsSaving(false);
  };

  /**
   * Handles saving item edits from the nested ItemEditor.
   * Re-fetches the item after a successful update so that the local `items`
   * map (and therefore `selectedItem` / `itemInfo`) reflects the latest data
   * even when the item wasn't previously in the store.
   * @param data - The updated item parameters
   * @returns `true` on success, or an error message string on failure
   */
  const handleItemUpdate = useCallback(
    async (data: UpdateItemParams): Promise<true | string> => {
      if (!effectiveItemId) {
        return "No item selected";
      }
      const { item, error } = await updateItem(effectiveItemId, data);
      if (!item) {
        return error ?? "Failed to update item";
      }
      await fetchItem(effectiveItemId, true);
      return true;
    },
    [updateItem, fetchItem, effectiveItemId],
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isEditing ? "Edit Line" : "Add Line"}</SheetTitle>
            <SheetDescription>
              {isEditing
                ? "Update the price breaks for this line."
                : "Select an item and define price breaks."}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 py-6">
            <div className="space-y-2">
              <Label>Item</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <ItemSelector
                    currentlySelectedId={itemId}
                    onSelectId={setItemId}
                    placeholder="Select item"
                    disabled={isEditing}
                    enableQuickCreate={!isEditing}
                  />
                </div>
                {selectedItem && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setIsItemEditorOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Pricing Unit</Label>
              <Select
                value={unit ?? "__same_as_item__"}
                onValueChange={(v) => {
                  if (v === "__same_as_item__") {
                    setUnit(null);
                    return;
                  }
                  const parsed = PricingUnitSchema.safeParse(v);
                  if (parsed.success) {
                    setUnit(parsed.data);
                  }
                }}
                disabled={allowedUnits.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      itemInfo
                        ? `Same as item (${formatItemUnit(itemInfo.unit)})`
                        : "Same as item"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__same_as_item__">
                    {itemInfo
                      ? `Same as item (${formatItemUnit(itemInfo.unit)})`
                      : "Same as item"}
                  </SelectItem>
                  {allowedUnits.map((u) => (
                    <SelectItem key={u} value={u}>
                      {formatPricingUnit(u)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {helperText && (
                <p className="text-xs text-muted-foreground">{helperText}</p>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Price Breaks</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddPriceBreak}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Break
                </Button>
              </div>

              {priceBreaks.map((priceBreak, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[1fr_1fr_auto] gap-3"
                >
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {unit ? `Min (${formatPricingUnit(unit)})` : "Min Qty"}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={String(priceBreak.minQty)}
                      onChange={(event) =>
                        handlePriceBreakChange(
                          index,
                          "minQty",
                          event.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {unit
                        ? `Unit Price per ${formatPricingUnit(unit)}`
                        : "Unit Price"}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={priceBreak.unitPrice}
                      onChange={(event) =>
                        handlePriceBreakChange(
                          index,
                          "unitPrice",
                          event.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemovePriceBreak(index)}
                      disabled={priceBreaks.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <SheetFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Update" : "Add"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {effectiveItemId && (
        <ItemEditor
          open={isItemEditorOpen}
          onOpenChange={setIsItemEditorOpen}
          itemId={effectiveItemId}
          onSave={handleItemUpdate}
          mode="edit"
        />
      )}
    </>
  );
}
